import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { AE_LOGO_URL, NAV_LINKS } from "@/lib/constants";

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="ae-nav"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-white/80 border-b border-black/5 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 group"
                >
                    <div className="relative h-10 w-10 rounded-full overflow-hidden shadow-ae-soft">
                        <img
                            src={AE_LOGO_URL}
                            alt="AE Logo"
                            className="h-full w-full object-cover scale-110 group-hover:scale-125 transition-transform duration-500"
                        />
                    </div>
                    <div className="leading-none">
                        <div className={`font-display font-bold text-base md:text-lg tracking-tight ${scrolled ? "text-ae-ink" : "text-white"}`}>
                            Amplified eSolutions
                        </div>
                        <div className={`text-[10px] uppercase tracking-[0.18em] font-medium mt-1 ${scrolled ? "text-ae-gray-500" : "text-white/60"}`}>
                            AI-Enhanced Advertising Agency
                        </div>
                    </div>
                </a>

                <nav className="hidden md:flex items-center gap-1">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.label}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={`relative px-4 py-2 text-sm font-medium hover:text-ae-orange transition-colors ${scrolled ? "text-ae-gray-700" : "text-white/80"}`}
                        >
                            <span>{l.label}</span>
                            <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-ae-orange scale-x-0 hover:scale-x-100 transition-transform origin-left" />
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="#contact"
                        data-testid="nav-cta-start-project"
                        className={`hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold hover:bg-ae-orange hover:text-white transition-all duration-300 group ${scrolled ? "bg-ae-charcoal text-white" : "bg-white text-ae-ink"}`}
                    >
                        Start a Project
                        <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform" />
                    </a>
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        className="md:hidden h-11 w-11 inline-flex items-center justify-center rounded-full border border-black/10 bg-white"
                        aria-label="Menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="md:hidden border-t border-black/5 bg-white/95 backdrop-blur-xl"
                        data-testid="nav-mobile-panel"
                    >
                        <div className="px-6 py-4 flex flex-col gap-1">
                            {NAV_LINKS.map((l) => (
                                <a
                                    key={l.label}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="py-3 text-base font-medium text-ae-gray-700 hover:text-ae-orange border-b border-black/5"
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#contact"
                                onClick={() => setOpen(false)}
                                className="mt-3 inline-flex items-center justify-center gap-2 bg-ae-charcoal text-white rounded-full px-5 py-3 text-sm font-semibold"
                                data-testid="nav-mobile-cta"
                            >
                                Start a Project <ArrowUpRight className="h-4 w-4" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
